import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest } from "@/api/client";
import { blobPut, compressImage, getSurvey, newId, putSurvey, queueAdd, queueUpsert } from "@/lib/fieldStore";
import { buildView, companyPayload } from "@/lib/fieldLocal";
import { useFieldSync } from "./FieldSync";

// Il sopralluogo sul tablet: ogni azione cambia SUBITO la copia locale e mette in coda l'operazione per il server.
// Cosi' tutto funziona senza rete e, quando la rete c'e', parte da solo.
export function useFieldSurvey(id) {
  const { pack, tick, kick, online } = useFieldSync();
  const [rec, setRec] = useState(null);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const busy = useRef(false);

  // rilegge la copia locale all'avvio e dopo ogni sincronizzazione
  useEffect(() => {
    let alive = true;
    getSurvey(id).then(async (r) => {
      if (!alive) return;
      if (r) { setRec(r); setLoaded(true); return; }
      // sopralluogo affidato dal consulente e mai aperto su questo tablet: lo scarico (serve la rete)
      if (!online) { setError("Questo sopralluogo non e' ancora sul tablet: collegati a internet per scaricarlo."); setLoaded(true); return; }
      try {
        const d = await apiRequest(`/field/surveys/${id}/state`);
        const copy = { id, company_id: d.view.survey.company_id, title: d.view.survey.title, company: d.company, meta: d.meta,
          document_kinds: d.document_kinds, task_codes: d.task_codes, answers: d.answers, documents: d.documents,
          equipment_units: d.equipment_units, send_queued: false, problems: [], rev: 0,
          server: { state: d.view.state, change_request: d.view.change_request, plans: d.view.plans, survey: { submitted: d.view.survey.submitted } } };
        await putSurvey(copy);
        if (alive) setRec(copy);
      } catch (e) { if (alive) setError(e.message); }
      if (alive) setLoaded(true);
    }).catch((e) => { if (alive) { setError(e.message); setLoaded(true); } });
    return () => { alive = false; };
  }, [id, tick, online]);

  const view = useMemo(() => (pack && rec ? buildView(pack, rec) : null), [pack, rec]);

  const mutate = useCallback(async (fn) => {
    const cur = await getSurvey(id);
    const next = fn(JSON.parse(JSON.stringify(cur)));
    next.rev = (cur.rev || 0) + 1;
    await putSurvey(next);
    setRec(next);
  }, [id]);

  const guard = useCallback(async (fn) => {
    if (busy.current) return undefined;
    busy.current = true;
    try { return await fn(); } finally { busy.current = false; kick(); }
  }, [kick]);

  const actions = useMemo(() => ({
    answer: (q, state, value, evidence) => guard(async () => {
      const data = { question_code: q.question_code, task_code: q.task_code, equipment_code: q.equipment_code, state,
        value: state === "answered" ? value : null, source: evidence.length ? "observed" : "declared", evidence };
      await mutate((r) => { r.answers[q.key] = { ...data, evidence_ids: evidence }; return r; });
      await queueUpsert({ type: "answer", survey_id: id, uid: newId(), ckey: `answer:${id}:${q.key}`, data });
    }),
    photo: async (q, req, file) => {
      const blob = await compressImage(file);
      const uuid = newId();
      await blobPut(uuid, blob);
      await queueAdd({ type: "blob", survey_id: id, uuid, meta: { kind: req.kind, question_code: q.question_code, task_code: q.task_code, equipment_code: q.equipment_code } });
      kick();
      return uuid;
    },
    document: (d, file, expires_on) => guard(async () => {
      const blob = await compressImage(file);
      const uuid = newId();
      await blobPut(uuid, blob);
      await queueAdd({ type: "blob", survey_id: id, uuid, meta: { kind: "document" } });
      await mutate((r) => { r.documents[d.key] = { status: "received", evidence_ids: [uuid], expires_on: expires_on || null, note: "" }; return r; });
      await queueUpsert({ type: "document", survey_id: id, uid: newId(), ckey: `doc:${id}:${d.key}`, data: { key: d.key, status: "received", evidence: uuid, expires_on: expires_on || null } });
    }),
    units: (task_code, equipment_code, units) => guard(async () => {
      const key = `${task_code}:${equipment_code}`;
      await mutate((r) => { if (units.length) r.equipment_units[key] = units.map((u) => ({ id: u.id, label: u.label || u.id, next_check: u.next_check || null })); else delete r.equipment_units[key]; return r; });
      await queueUpsert({ type: "units", survey_id: id, uid: newId(), ckey: `units:${id}:${key}`, data: { task_code, equipment_code, units } });
    }),
    data: (company, meta) => guard(async () => {
      await mutate((r) => { r.company = { ...r.company, ...company }; r.meta = { ...r.meta, ...meta }; r.title = r.title || company.business_name; return r; });
      await queueUpsert({ type: "company", survey_id: id, uid: newId(), ckey: `company:${id}`, data: companyPayload(company) });
      await queueUpsert({ type: "meta", survey_id: id, uid: newId(), ckey: `meta:${id}`, data: { meta } });
    }),
    send: () => guard(async () => {
      await mutate((r) => { r.send_queued = true; return r; });
      await queueUpsert({ type: "send", survey_id: id, uid: newId(), ckey: `send:${id}`, data: {} });
    }),
    clearProblems: () => mutate((r) => { r.problems = []; return r; }),
  }), [id, guard, mutate, kick]);

  return { view, rec, loaded, error, actions, online, hasPack: !!pack };
}
